import React, { useState, useRef, useEffect } from "react";
import { Shield, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Discrete shield icon (top-left beside the logo) that opens a passphrase prompt.
 * On correct passphrase, logs the user in as admin and navigates to /admin.
 */
export default function AdminShield() {
  const [open, setOpen] = useState(false);
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pass.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/admin-shield", { passphrase: pass });
      loginWithToken(r.data.token, r.data.user);
      toast.success("Connexion admin activée.");
      setOpen(false);
      setPass("");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Passphrase incorrecte");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shrink-0 w-7 h-7 grid place-items-center rounded-sm bg-foreground/90 hover:bg-foreground text-background opacity-60 hover:opacity-100 transition-all"
        title="Admin"
        aria-label="Accès admin"
        data-testid="admin-shield-btn"
      >
        <Shield size={14} weight="fill" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm grid place-items-center p-6"
          onClick={() => !busy && setOpen(false)}
          data-testid="admin-shield-modal"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-card border border-border rounded-sm p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-foreground">
                <Shield size={18} weight="fill" />
                <span className="font-display font-semibold">Accès admin</span>
              </div>
              <button type="button" onClick={() => setOpen(false)} disabled={busy} className="text-muted-foreground hover:text-foreground" data-testid="admin-shield-close">
                <X size={18} weight="bold" />
              </button>
            </div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground block mb-1">
              Passphrase
            </label>
            <input
              ref={inputRef}
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="••••••••"
              autoComplete="off"
              className="w-full border border-border rounded-sm px-3 py-2.5 text-sm bg-background outline-none focus:border-primary"
              data-testid="admin-shield-input"
              disabled={busy}
            />
            <div className="flex items-center gap-2 mt-4">
              <button
                type="submit"
                disabled={busy || !pass.trim()}
                className="flex-1 bg-foreground text-background hover:bg-foreground/90 px-4 py-2.5 rounded-sm font-semibold text-sm disabled:opacity-50"
                data-testid="admin-shield-submit"
              >
                {busy ? "…" : "OK"}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="px-4 py-2.5 border border-border rounded-sm text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
